/**
 * Frontend → Supabase target guard.
 *
 * Vite inlines VITE_* values at build time. A production static bundle cannot
 * be retargeted later by Docker/Caddy env vars unless rebuilt.
 *
 * Known project refs below are public identifiers, not secrets.
 */
export const PRODUCTION_SUPABASE_PROJECT_REF = "xnifwxitiwkrtnyswecu";
// Retained only to identify and reject the retired hosted test project.
export const E2E_SUPABASE_PROJECT_REF = "mcdzvcnebqxyddqoplqc";
export const LEGACY_TEMP_PRODUCTION_SUPABASE_PROJECT_REF = "uvvnhcpbbmwpyntpbbgl";

/** @deprecated Use PRODUCTION_SUPABASE_PROJECT_REF. xnif is canonical production. */
export const READY_AI_SUPABASE_PROJECT_REF = PRODUCTION_SUPABASE_PROJECT_REF;

export type FrontendBackendConfigInput = {
  url: string | undefined;
  anonKey: string | undefined;
  allowE2EBackend?: boolean;
};

export class FrontendBackendConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FrontendBackendConfigError";
  }
}

function isDisposableLocalSupabase(parsed: URL): boolean {
  return (parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost")
    && parsed.protocol === "http:"
    && parsed.port === "54321";
}

function projectRefFromUrl(url: string): string | null {
  try {
    const host = new URL(url).hostname.toLowerCase();
    const match = host.match(/^([a-z0-9]+)\.supabase\.co$/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

export function assertFrontendBackendConfig(input: FrontendBackendConfigInput): void {
  const url = input.url?.trim() ?? "";
  const anonKey = input.anonKey?.trim() ?? "";

  if (!url || !anonKey) {
    throw new FrontendBackendConfigError(
      "Production frontend builds require VITE_PUBLIC_SUPABASE_URL and VITE_PUBLIC_SUPABASE_ANON_KEY. These values are inlined at Vite build time; they cannot be injected later by Caddy. Do not fall back to a default backend.",
    );
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new FrontendBackendConfigError("VITE_PUBLIC_SUPABASE_URL must be a valid absolute URL.");
  }

  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new FrontendBackendConfigError("VITE_PUBLIC_SUPABASE_URL must be an http(s) URL.");
  }

  const ref = projectRefFromUrl(url);
  const mentions = (projectRef: string) => ref === projectRef || url.includes(projectRef);

  if (mentions(LEGACY_TEMP_PRODUCTION_SUPABASE_PROJECT_REF)) {
    throw new FrontendBackendConfigError(
      "Refusing to build: VITE_PUBLIC_SUPABASE_URL targets the legacy temporary production Supabase project, which is forbidden.",
    );
  }

  if (mentions(E2E_SUPABASE_PROJECT_REF)) {
    throw new FrontendBackendConfigError(
      "Refusing to build: the retired hosted E2E project is disabled. Use Borraq-test for hosted builds or the disposable local Auth/REST stack for CI.",
    );
  }

  if (input.allowE2EBackend) {
    if (mentions(PRODUCTION_SUPABASE_PROJECT_REF)) {
      throw new FrontendBackendConfigError(
        "Refusing to build: TRADIVO_ALLOW_E2E_BACKEND cannot be set for the production Supabase project.",
      );
    }
    if (isDisposableLocalSupabase(parsed)) {
      return;
    }
    throw new FrontendBackendConfigError(
      "Refusing to build: TRADIVO_ALLOW_E2E_BACKEND is set, so VITE_PUBLIC_SUPABASE_URL must target the disposable local Auth/REST stack.",
    );
  }

  if (ref !== null && ref !== PRODUCTION_SUPABASE_PROJECT_REF) {
    throw new FrontendBackendConfigError(
      "Refusing to build: VITE_PUBLIC_SUPABASE_URL must target the canonical Tradivo production Supabase project.",
    );
  }

  if (ref === PRODUCTION_SUPABASE_PROJECT_REF) {
    return;
  }

  // Non-supabase.co hosts (CI compile placeholders) are allowed. Any other
  // supabase.co ref was already rejected above.
}
